import { InfoTabHeaderWithLock } from './info-tab-header-with-lock'
import { ProgrammeAvailability } from './programme-availability'
import { WorkoutCard } from './programme-workout-card'
import styles from './programme-workouts-container.module.scss'
import { ProgrammeIntensityPill } from '../programme-intensity-pill/programme-intensity-pill'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Location, useLocation, useNavigate } from 'react-router-dom'
import { CompletedProgrammeWorkout, HustlerProgramme, InfoTabItem, Intensity, ProgrammeWorkout } from 'src/dao/core-dao'
import { useSuggestedSeries } from 'src/hook/use-suggested-series'
import { getHighlightWorkoutIdFromSearch } from 'src/util/build-programme-overview-url'
import { consumePendingProgrammeOverviewHighlight } from 'src/util/programme-overview-pending-highlight'
import { timeUtil } from 'src/util/time-util'
import { selectedWeekWorkouts } from 'src/util/week-workouts'

interface WorkoutsContainerProps {
  selectWorkout: (programmeWorkout: ProgrammeWorkout) => void
  programmeWorkouts: ProgrammeWorkout[]
  completedProgrammeWorkouts: CompletedProgrammeWorkout[]
  hustlerProgramme: HustlerProgramme
  programmeIntensities: Intensity[]
}

const getWeekFromURL = (location: Location): number | undefined => {
  const queryParams = new URLSearchParams(location.search)
  const weekParam = queryParams.get('week')
  if (weekParam) {
    const weekNumber = parseInt(weekParam, 10)
    if (!isNaN(weekNumber) && weekNumber > 0) {
      return weekNumber
    }
  }
  return
}

const setWeekInURL = (
  navigate: ReturnType<typeof useNavigate>,
  location: Location,
  weekNumber: number,
  replace = false,
): void => {
  const queryParams = new URLSearchParams(location.search)
  queryParams.set('week', weekNumber.toString())
  navigate(`${location.pathname}?${queryParams.toString()}`, { replace })
}

export const WorkoutsContainer = ({
  selectWorkout,
  hustlerProgramme,
  programmeWorkouts,
  completedProgrammeWorkouts,
  programmeIntensities,
}: WorkoutsContainerProps): React.ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const { nextSeriesWorkout, suggestedSeries } = useSuggestedSeries()
  const lastScrolledHighlightSearchRef = useRef<string | null>(null)
  const startedAt = parseInt(hustlerProgramme.startedAt)

  useEffect(() => {
    lastScrolledHighlightSearchRef.current = null
  }, [location.pathname])
  const currentProgrammeId = hustlerProgramme.programme.id
  const suggestedHighlightWorkoutId = suggestedSeries?.id === currentProgrammeId ? nextSeriesWorkout?.workout?.id : undefined
  const highlightWorkoutId = getHighlightWorkoutIdFromSearch(location.search) ?? suggestedHighlightWorkoutId
  const initialWeekNumber = getWeekFromURL(location)
  const initialWeek = hustlerProgramme.programme.weekDescription.find((w) => w.order === initialWeekNumber) ||
    hustlerProgramme.programme.weekDescription[0] || { description: '', order: 1, title: '' }
  const [selectedWeek, setSelectedWeek] = useState<InfoTabItem>(initialWeek)
  const selectedWorkouts = selectedWeekWorkouts(selectedWeek, programmeWorkouts)

  useEffect(() => {
    const consumed = consumePendingProgrammeOverviewHighlight(currentProgrammeId, navigate, location)
    if (!consumed) {
      const weekNumber = getWeekFromURL(location)
      if (!weekNumber && selectedWeek.order) {
        setWeekInURL(navigate, location, selectedWeek.order, true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  useEffect(() => {
    const weekNumber = getWeekFromURL(location)
    if (weekNumber && weekNumber !== selectedWeek.order) {
      const weekToSet = hustlerProgramme.programme.weekDescription.find((w) => w.order === weekNumber) || selectedWeek
      setSelectedWeek(weekToSet)
    }
  }, [location, hustlerProgramme.programme.weekDescription, selectedWeek])

  useEffect(() => {
    const fromUrl = getHighlightWorkoutIdFromSearch(location.search)
    if (!fromUrl) {
      return
    }
    const visible = selectedWorkouts.some((w) => w.workout?.id === fromUrl)
    if (!visible) {
      return
    }
    if (lastScrolledHighlightSearchRef.current === location.search) {
      return
    }
    const el = document.querySelector(`[data-programme-workout-highlight="${fromUrl}"]`)
    if (!el) {
      return
    }
    lastScrolledHighlightSearchRef.current = location.search
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }, [location.search, selectedWorkouts])

  const handleWeekChange = (newWeek: InfoTabItem): void => {
    setSelectedWeek(newWeek)
    const queryParams = new URLSearchParams(location.search)
    if (newWeek && typeof newWeek.order === 'number' && newWeek.order > 0) {
      queryParams.set('week', newWeek.order.toString())
    } else {
      queryParams.set('week', '1')
    }
    navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true })
  }

  const getIntensity = (intensityId: string): Intensity | undefined => {
    return programmeIntensities.find((i) => i.id === intensityId)
  }

  const isProgrammeWorkoutCompleted = useCallback(
    (workoutId: string): boolean => {
      if (!completedProgrammeWorkouts) return false
      return completedProgrammeWorkouts
        .map((cpw) => cpw.completedWorkout.workout?.id)
        .filter(Boolean)
        .map((id) => id!)
        .includes(workoutId)
    },
    [completedProgrammeWorkouts],
  )

  const programCompletedText = useCallback((): string => {
    if (!selectedWorkouts) return '0/0'
    const completedCount = selectedWorkouts.filter((pw) =>
      completedProgrammeWorkouts?.map((cpw) => cpw.programmeWorkout.id).includes(pw.id),
    ).length
    return `${completedCount}/${selectedWorkouts.length}`
  }, [completedProgrammeWorkouts, selectedWorkouts])

  return (
    <div className={styles.container}>
      {hustlerProgramme?.programme?.weekDescription && (
        <InfoTabHeaderWithLock
          items={hustlerProgramme.programme.weekDescription}
          selectedItem={selectedWeek}
          onClick={handleWeekChange}
          isTabLocked={() => false}
        />
      )}
      <div className={styles.workoutsDetail}>
        <div className={styles.completedWorkouts}>
          <span>{programCompletedText()}</span>
          <span>completed</span>
        </div>
        <ProgrammeAvailability
          className={styles.weekDate}
          startedAt={timeUtil.addWeeks(startedAt, selectedWeek?.order - 1).getTime()}
          numberOfWeeks={1}
        />
        <div className={styles.programmeIntensityPillContainer}>
          <ProgrammeIntensityPill intensity={getIntensity(hustlerProgramme.programme.intensityId)} />
        </div>
      </div>
      {programmeWorkouts && (
        <>
          {selectedWorkouts.map((item) => (
            <WorkoutCard
              key={item.id}
              workout={item.workout}
              isLocked={false}
              isCompleted={isProgrammeWorkoutCompleted(item.workout?.id)}
              selectWorkout={() => selectWorkout(item)}
              nextSeriesWorkoutId={highlightWorkoutId}
            />
          ))}
        </>
      )}
    </div>
  )
}
